/**
 * Pure pieces of the voice-note recorder (the browser-facing hook is
 * `hooks/use-voice-note-recorder.ts`).
 */
import { VOICE_NOTE_MIME_TYPE } from "@shared/extensions/voice-note/file"

/**
 * Milliseconds of *recorded* audio. Paused time is excluded so the clock lines
 * up with `audio.currentTime` on playback (MediaRecorder keeps paused audio
 * contiguous). `now` is injectable for tests.
 */
export class RecordingClock {
  private accumulatedMs = 0
  private segmentStartedAt: number | null = null

  constructor(private readonly nowFn: () => number = () => performance.now()) {}

  start(): void {
    this.accumulatedMs = 0
    this.segmentStartedAt = this.nowFn()
  }

  pause(): void {
    if (this.segmentStartedAt === null) return
    this.accumulatedMs += this.nowFn() - this.segmentStartedAt
    this.segmentStartedAt = null
  }

  resume(): void {
    if (this.segmentStartedAt !== null) return
    this.segmentStartedAt = this.nowFn()
  }

  get isRunning(): boolean {
    return this.segmentStartedAt !== null
  }

  /** Elapsed recorded milliseconds. */
  now(): number {
    const running = this.segmentStartedAt === null ? 0 : this.nowFn() - this.segmentStartedAt
    return this.accumulatedMs + running
  }
}

/** Why recording is unavailable in this browser, or null when it works. */
export function getVoiceRecordingUnsupportedReason(): string | null {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    return "This browser cannot access the microphone."
  }
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported(VOICE_NOTE_MIME_TYPE)) {
    return "Recording needs Chrome, Edge, Firefox or Safari 18.4+ (WebM/Opus audio)."
  }
  return null
}

/** Concatenate MediaRecorder chunks into one byte array. */
export async function blobsToBytes(chunks: Blob[], type: string): Promise<Uint8Array> {
  const buffer = await new Blob(chunks, { type }).arrayBuffer()
  return new Uint8Array(buffer)
}

/** `getUserMedia` constraints for the chosen device (`null` = system default). */
export function buildAudioConstraints(deviceId: string | null): MediaStreamConstraints {
  return {
    audio: {
      ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true
    }
  }
}

/** How long a microphone is watched for a dead input before alerting. */
export const DEAD_INPUT_CHECK_MS = 10_000
const DEAD_INPUT_SAMPLE_INTERVAL_MS = 500

/**
 * Watch a freshly opened microphone stream and alert (plain `window.alert`)
 * when the input never moved during the first `DEAD_INPUT_CHECK_MS`. A
 * microphone the OS is not capturing from (laptop lid closed, webcam mic idle
 * until its camera streams, virtual loopback) opens without any error and reads
 * exactly 0 / 0 on every sample; a live mic in a quiet room still shows a small
 * non-zero spread, and a Bluetooth headset reads zero only for its first moment
 * while switching profiles. The extremes are taken over the whole window, so
 * that startup gap does not trigger it. Used by the "Test" preview and by the
 * recorder. Returns a stop function; stopping before the window ends cancels
 * the check.
 */
export function watchDeadInput(stream: MediaStream): () => void {
  if (typeof AudioContext === "undefined") return () => {}
  let context: AudioContext
  try {
    context = new AudioContext()
  } catch {
    return () => {}
  }
  const source = context.createMediaStreamSource(stream)
  const analyser = context.createAnalyser()
  analyser.fftSize = 8192
  source.connect(analyser)
  const samples = new Float32Array(analyser.fftSize)
  const label = stream.getAudioTracks()[0]?.label || "The selected microphone"
  void context.resume().catch(() => {})

  let overallMin = Number.POSITIVE_INFINITY
  let overallMax = Number.NEGATIVE_INFINITY
  const sample = () => {
    if (context.state !== "running") return
    analyser.getFloatTimeDomainData(samples)
    for (let i = 0; i < samples.length; i++) {
      const v = samples[i] as number
      if (v < overallMin) overallMin = v
      if (v > overallMax) overallMax = v
    }
  }
  const timer = setInterval(sample, DEAD_INPUT_SAMPLE_INTERVAL_MS)
  const stop = () => {
    clearInterval(timer)
    clearTimeout(check)
    source.disconnect()
    void context.close().catch(() => {})
  }
  const check = setTimeout(() => {
    sample()
    stop()
    // Never sampled (context stayed suspended) is not evidence of a dead mic.
    if (overallMax === Number.NEGATIVE_INFINITY) return
    if (overallMax === 0 || overallMax === overallMin) {
      window.alert(
        `No audio is coming from "${label}": the input read exactly zero for ${DEAD_INPUT_CHECK_MS / 1000} seconds, ` +
          "so it is capturing silence. A laptop microphone is off while the lid is closed, and a webcam microphone " +
          "may only work while its camera is streaming. Fix the device or pick another microphone in the Voice tab."
      )
    }
  }, DEAD_INPUT_CHECK_MS)
  return stop
}
